'use no memo'

import type { Table } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { AppSelect } from '~/components/new/app-ui/app-select'
import { Button } from '~/components/ui/button'

const PAGE_SIZES = [10, 20, 25, 30, 40, 50] as const

interface AppTableFooterPaginationProps {
  table: Table<any>
  /** Tailles de page proposées. Par défaut la liste commune à tous les tableaux. */
  pageSizes?: readonly number[]
}

export function AppTableFooterPagination({
  table,
  pageSizes = PAGE_SIZES,
}: AppTableFooterPaginationProps) {
  return (
    <div className="ml-auto flex items-center space-x-6 lg:space-x-8">
      <div className="flex items-center space-x-2">
        <p className="font-medium text-sm whitespace-nowrap">Lignes par page</p>
        <AppSelect
          options={pageSizes.map((pageSize) => ({
            label: `${pageSize}`,
            value: pageSize,
          }))}
          onChange={(value) => (value ? table.setPageSize(value) : undefined)}
          value={table.getState().pagination.pageSize}
        />
      </div>

      <div className="flex w-[100px] items-center justify-center font-medium text-sm">
        Page {table.getState().pagination.pageIndex + 1} sur {table.getPageCount()}
      </div>

      <div className="flex items-center space-x-2">
        <Button
          className="hidden size-8 lg:flex"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.setPageIndex(0)}
          size="icon"
          variant="outline"
        >
          <span className="sr-only">Aller à la première page</span>
          <ChevronsLeft />
        </Button>

        <Button
          className="size-8"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
          size="icon"
          variant="outline"
        >
          <span className="sr-only">Aller à la page précédente</span>
          <ChevronLeft />
        </Button>

        <Button
          className="size-8"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
          size="icon"
          variant="outline"
        >
          <span className="sr-only">Aller à la page suivante</span>
          <ChevronRight />
        </Button>

        <Button
          className="hidden size-8 lg:flex"
          disabled={!table.getCanNextPage()}
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          size="icon"
          variant="outline"
        >
          <span className="sr-only">Aller à la dernière page</span>
          <ChevronsRight />
        </Button>
      </div>
    </div>
  )
}
